import * as React from 'react';

// Local Resources
import ImageButton from '../imagebutton';
import {
	ANALYSIS_RESULT,
	ANALYSIS_TYPE,
	formatDate,
	isValidDate,
	AnalysisResultToIconState,
	MeasurementStatetoHTML,
} from '../enums';

// MaterialUI

// Tippy.JS Resources
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import 'tippy.js/animations/shift-away.css';


const UNIT_SHAPE = {
	HORIZONTAL: 'h',
	VERTICAL: 'v',
	SQUARE: 's'
};

const DIM_SHORT = 51;
const DIM_LONG = 100;

function UnitShapeToLetter(unitShape) {
	switch (unitShape) {
		case 0:
			return UNIT_SHAPE.SQUARE;

		case 1:
			return UNIT_SHAPE.HORIZONTAL;

		case 2:
			return UNIT_SHAPE.VERTICAL;
			
		default:
			return UNIT_SHAPE.VERTICAL;
	}
}
			
function GetUnitSize(unitShape, scale = 1.0) {

	let shortDimension = DIM_SHORT * scale;
	let longDimension = DIM_LONG * scale;

	if (unitShape) {
		switch (unitShape) {
			case UNIT_SHAPE.VERTICAL:
				return [shortDimension, longDimension];

			case UNIT_SHAPE.HORIZONTAL:
				return [longDimension, shortDimension];

			case UNIT_SHAPE.SQUARE:
				return [longDimension, longDimension];

			default:
				return [shortDimension, longDimension]; // Default is vertical
		}
	} else {
			return [shortDimension, longDimension]; // Default is vertical
	}
}

function popoverSectionStyle(sectionHeading, sectionContent) {
	return (
		<div style={{ 
			borderTop: '1px solid #404040',
			paddingTop: '8px',
			marginTop: '8px'
		}}>
			<div style={{ 
				fontSize: '0.8em', 
				color: '#808080',
				marginBottom: '6px',
				textTransform: 'uppercase',
				letterSpacing: '0.5px'
			}}>
				{sectionHeading}
			</div>
			<div style={{ 
				fontSize: '0.9em', 
				color: '#b0b0b0',
				marginLeft: '8px'
			}}>
				{sectionContent}
			</div>
		</div>
	);
}

function BuildPopover(stringNum, unitNum, unitState, unitDates) {
	const unitID = `String ${stringNum} Unit ${unitNum}`;
	
	function formatUnitDates() {
		const dateDict = [
			{ label: 'Install Date', value: unitDates.install },
			{ label: 'Date Code', value: unitDates.manufacture },
			{ label: 'Warranty Exp.', value: unitDates.warranty_expiration }
		];

		const validDates = dateDict.filter(date => isValidDate(date.value));
		
		if (validDates.length === 0) return <span style={{ color: '#606060' }}>No valid battery date information available</span>;

		return validDates.map((date, index) => {
			const isWarrantyExpired = date.label === 'Warranty Exp.' && new Date() > new Date(date.value);
			
			return (
				<div key={index} style={{ marginBottom: '3px' }}>
					<span style={{ color: '#909090' }}>{date.label}:</span> {formatDate(date.value)}
					{isWarrantyExpired && <span style={{ color: '#ff6b6b', marginLeft: '6px' }}>(Expired)</span>}
				</div>
			);
		});
	}
	
	const voltage = MeasurementStatetoHTML('Unit', unitState.voltage, ANALYSIS_TYPE.VOLTAGE);
	const impedance = MeasurementStatetoHTML('Unit', unitState.impedance, ANALYSIS_TYPE.IMPEDANCE);
	const temperature = MeasurementStatetoHTML('Unit', unitState.temperature, ANALYSIS_TYPE.TEMPERATURE);
	const diffTemp = MeasurementStatetoHTML('Unit', unitState.diff_temperature, ANALYSIS_TYPE.DIFF_TEMPERATURE);

	const showDiffTemp = unitState.diff_temperature.result !== ANALYSIS_RESULT.ANALYSIS_WITHIN_LIMITS;

	const measurementsSection = (
		<div>
			<div style={{ marginBottom: '3px' }}>{voltage}</div>
			<div style={{ marginBottom: '3px' }}>{impedance}</div>
			<div style={{ marginBottom: '3px' }}>{temperature}</div>
			{showDiffTemp && <div style={{ marginBottom: '3px' }}>{diffTemp}</div>}
		</div>
	);

	const datesSection = formatUnitDates();
	
	return (
		<div
			style={{
				fontFamily: 'Saira, sans-serif',
				fontWeight: '600',
				minWidth: '250px'
			}}
		>
			{/* Header */}
			<div style={{ 
				fontSize: '1.05em',
				marginBottom: '4px'
			}}>
				{unitID}
			</div>
			
			{/* Measurements Section */}
			{popoverSectionStyle('Measurements', measurementsSection)}
			
			{/* Dates Section */}
			{popoverSectionStyle('Battery Details', datesSection)}
		</div>
	);
}

export default function UnitIcon(props) {

	const {locationNum, stringNum, unitNum, unitShape, unitState, unitDates, unitAlarms, scale = 1.0} = props;

	const shape = UnitShapeToLetter(unitShape);
	const state = AnalysisResultToIconState(unitState.overall);

	const imgPath = `/images/batterymap_icons/unit_${shape}_${state}.svg`;

	const [ width, height ] = GetUnitSize(shape, scale);

	const popover = BuildPopover(stringNum, unitNum, unitState, unitDates);

	return (
		<Tippy
			content={popover}
			placement="bottom"
			animation="shift-away"
		>
			<span tabIndex="0">
				<ImageButton
					imgUrl={imgPath}
					imgWidth={width}
					imgHeight={height}
					buttonText={unitNum}
					linkUrl={`/location/${locationNum}/trend/unit?string=${stringNum}&unit=${unitNum}`}
					scale={scale}
				/>
			</span>
		</Tippy>
	);
}